import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  Factory,
  Printer,
  Download,
  Calendar,
  Package
} from 'lucide-react';
import { format } from 'date-fns';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Production = () => {
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(
    searchParams.get('date') ? new Date(searchParams.get('date')) : new Date()
  );
  const [tiffinCount, setTiffinCount] = useState(100);
  const [productionPlan, setProductionPlan] = useState(null);
  const [recentPlans, setRecentPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchRecentPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/production/plans`);
      setRecentPlans(response.data.slice(0, 10));
    } catch (error) {
      // Non-blocking; list stays empty
    }
  }, []);

  const loadPlan = useCallback(async (planId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/production/plans/${planId}`);
      setProductionPlan(response.data);
      setSelectedDate(new Date(response.data.date));
      setTiffinCount(response.data.tiffin_count);
    } catch (error) {
      toast.error('Failed to load production plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentPlans();
  }, [fetchRecentPlans]);

  useEffect(() => {
    const planId = searchParams.get('plan');
    if (planId) {
      loadPlan(planId);
    }
  }, [searchParams, loadPlan]);

  const generatePlan = async () => {
    if (tiffinCount <= 0) {
      toast.error('Tiffin count must be greater than 0');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/production/generate`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        tiffin_count: parseInt(tiffinCount)
      });

      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }

      setProductionPlan(response.data);
      toast.success('Production plan generated');
      fetchRecentPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const exportCSV = () => {
    if (!productionPlan) return;

    let csv = 'Production Plan\n';
    csv += `Date,${productionPlan.date}\n`;
    csv += `Tiffins,${productionPlan.tiffin_count}\n\n`;

    csv += 'Combined Ingredients\n';
    csv += 'Ingredient,Quantity,Unit\n';
    productionPlan.combined_ingredients.forEach(ing => {
      csv += `${ing.ingredient_name},${ing.quantity},${ing.unit}\n`;
    });

    csv += '\nDish-wise Breakdown\n';
    productionPlan.dishes.forEach(dish => {
      csv += `\n${dish.recipe_name} (${dish.category})\n`;
      csv += 'Ingredient,Quantity,Unit\n';
      dish.ingredients.forEach(ing => {
        csv += `${ing.ingredient_name},${ing.quantity},${ing.unit}\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production_${productionPlan.date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header - Hidden on print */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 no-print">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight" >
            Production
          </h1>
          <p className="text-stone-500 mt-1">Generate and print production plans</p>
        </div>
      </div>

      {/* Generator Card - Hidden on print */}
      <div className="virsa-card p-6 no-print">
        <h2 className="text-xl font-bold text-stone-800 mb-4" >
          GENERATE Production PLAN
        </h2>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-stone-600 mb-2">Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="virsa-input w-full text-left flex items-center justify-between"
                  data-testid="date-picker-btn"
                >
                  <span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                  <Calendar size={18} className="text-stone-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-stone-600 mb-2">Tiffin Count</label>
            <input
              type="number"
              min="1"
              value={tiffinCount}
              onChange={(e) => setTiffinCount(e.target.value)}
              className="virsa-input"
              data-testid="tiffin-count-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={generatePlan}
              disabled={generating}
              className="btn-primary h-12 px-8"
              data-testid="generate-btn"
            >
              {generating ? <span className="spinner" /> : <Factory size={20} />}
              <span>Generate</span>
            </button>
          </div>
        </div>
      </div>

      {/* Production Plan Output */}
      {productionPlan && (
        <div className="print-container">
          {/* Print Header */}
          <div className="virsa-card p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 no-print">
              <div>
                <h2 className="text-2xl font-bold text-stone-800" >
                  Production PLAN
                </h2>
                <p className="text-stone-500">
                  {format(new Date(productionPlan.date), 'EEEE, MMMM d, yyyy')} • {productionPlan.tiffin_count} Tiffins
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={handlePrint} className="btn-outline">
                  <Printer size={18} />
                  Print
                </button>
                <button onClick={exportCSV} className="btn-primary">
                  <Download size={18} />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Print-only header */}
            <div className="hidden print:block mb-6">
              <h1 className="text-3xl font-bold text-center" >
                THE VIRSA TIFFIN - Production PLAN
              </h1>
              <p className="text-center text-lg mt-2">
                {format(new Date(productionPlan.date), 'EEEE, MMMM d, yyyy')} • {productionPlan.tiffin_count} Tiffins
              </p>
            </div>

            {/* Combined Ingredients */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2" >
                <Package size={20} className="text-[#124242]" />
                Master Ingredient List
              </h3>
              <div className="overflow-x-auto">
                <table className="virsa-table print-table">
                  <thead>
                    <tr>
                      <th className="w-8">#</th>
                      <th>Ingredient</th>
                      <th className="text-right w-32">Quantity</th>
                      <th className="w-20">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionPlan.combined_ingredients.map((ing, index) => (
                      <tr key={`${ing.ingredient_name}-${ing.unit}`}>
                        <td className="text-stone-400">{index + 1}</td>
                        <td className="font-medium">{ing.ingredient_name}</td>
                        <td className="text-right font-bold text-lg kitchen-display">{ing.quantity}</td>
                        <td>{ing.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Dish-wise Breakdown */}
            {productionPlan.dishes.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-stone-800 mb-4" >
                  Dish-wise Breakdown
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {productionPlan.dishes.map((dish, index) => (
                    <div key={dish.recipe_name} className="border border-stone-200 rounded-lg overflow-hidden">
                      <div className="bg-[#124242] text-white p-3">
                        <h4 className="font-bold">{dish.recipe_name}</h4>
                        <span className="text-sm text-white/80">{dish.category}</span>
                      </div>
                      <div className="p-3">
                        <table className="w-full text-sm">
                          <tbody>
                            {dish.ingredients.map((ing, i) => (
                              <tr key={`${ing.ingredient_name}-${ing.unit}`} className="border-b border-stone-100 last:border-0">
                                <td className="py-1">{ing.ingredient_name}</td>
                                <td className="py-1 text-right font-semibold">
                                  {ing.quantity} {ing.unit}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {productionPlan.dishes.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <p>No dishes scheduled for this date.</p>
                <p className="text-sm mt-1">Set up rotations or add overrides in the Live Calendar.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Plans - Hidden on print */}
      {recentPlans.length > 0 && (
        <div className="virsa-card p-6 no-print">
          <h2 className="text-xl font-bold text-stone-800 mb-4" >
            RECENT Production PLANS
          </h2>
          <div className="space-y-2">
            {recentPlans.map(plan => (
              <button
                key={plan.id}
                onClick={() => loadPlan(plan.id)}
                className="w-full p-3 text-left bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors flex items-center justify-between"
                data-testid={`recent-plan-btn-${plan.id}`}
              >
                <div>
                  <p className="font-semibold text-stone-800">
                    {format(new Date(plan.date), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm text-stone-500">
                    {plan.tiffin_count} tiffins • {plan.dishes?.length || 0} dishes
                  </p>
                </div>
                <span className="badge badge-teal">
                  {format(new Date(plan.created_at), 'MMM d, HH:mm')}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
